import { useMemo } from 'react';
import { useAttributesStore } from '../../../../stores/prioritization/attributesStore';
import { useItemsStore } from '../../../../stores/prioritization/itemsStore';
import { C } from '../../theme/participantTokens';

const BAR_W = 50;
const BAR_H = 5;
const SLOT_X = [6, 70, 138] as const;

interface ItemCriteriaRadarProps {
  itemId?: string;
  criterionScores: Record<string, number>;
  criteriaIds: string[];
  compositeScore?: number;
  maxCriteria?: number;
}

function formatCriterionLabel(label: string): string {
  const trimmed = label.trim();
  if (trimmed.length <= 7) return trimmed.toUpperCase();
  return trimmed.slice(0, 7).toUpperCase();
}

function barWidth(score: number, max = 10): number {
  return Math.max(2, Math.round((Math.min(score, max) / max) * BAR_W));
}

function scoreColor(score: number, isHighest: boolean): string {
  if (isHighest && score >= 7) return C.coral;
  if (score >= 7) return C.coral;
  if (score >= 5) return C.mustard;
  return C.textSec;
}

export function ItemCriteriaRadar({
  itemId,
  criterionScores,
  criteriaIds,
  compositeScore,
  maxCriteria = 3,
}: ItemCriteriaRadarProps) {
  const getAttributeByKey = useAttributesStore(s => s.getAttributeByKey);
  const getItemById = useItemsStore(s => s.getItemById);

  const entries = useMemo(() => {
    const item = itemId ? getItemById(itemId) : undefined;
    const attrs = item && 'attributes' in item ? (item as { attributes?: Array<{ attributeKey: string; scoredValue?: number; value?: unknown }> }).attributes : undefined;

    return criteriaIds.slice(0, maxCriteria).map(cid => {
      let score = criterionScores[cid];
      if (typeof score !== 'number' && attrs) {
        const av = attrs.find(a => a.attributeKey === cid);
        if (typeof av?.scoredValue === 'number') score = av.scoredValue;
        else if (typeof av?.value === 'number') score = av.value;
      }
      return {
        id: cid,
        label: getAttributeByKey(cid)?.label ?? cid,
        score: typeof score === 'number' ? score : undefined,
      };
    }).filter(e => typeof e.score === 'number');
  }, [criteriaIds, criterionScores, getAttributeByKey, getItemById, itemId, maxCriteria]);

  if (entries.length === 0 && compositeScore == null) return null;

  const composite = compositeScore ?? entries.reduce((s, e) => s + (e.score ?? 0), 0) / Math.max(entries.length, 1);
  const compWidth = barWidth(composite);
  const maxScore = Math.max(...entries.map(e => e.score ?? 0), composite);

  return (
    <svg width="100%" height={80} viewBox="0 0 180 80" preserveAspectRatio="xMinYMid meet" aria-hidden>
      <defs>
        <filter id="pp-radar-glow">
          <feGaussianBlur stdDeviation="1" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {entries.map((e, i) => {
        const x = SLOT_X[i] ?? 6 + i * 64;
        const score = e.score ?? 0;
        const w = barWidth(score);
        const isHighest = score === maxScore && score >= 7;
        const fill = scoreColor(score, isHighest);
        const labelX = x;
        const valX = x + w + 2;
        return (
          <g key={e.id}>
            <text x={labelX} y={11} fontFamily="JetBrains Mono" fontSize={11} fill={C.textDim} letterSpacing={0.5}>
              {formatCriterionLabel(e.label)}
            </text>
            <rect x={x} y={18} width={BAR_W} height={BAR_H} rx={2} fill={C.ruleBright} />
            <rect
              x={x}
              y={18}
              width={w}
              height={BAR_H}
              rx={2}
              fill={fill}
              opacity={isHighest ? 0.65 : score >= 5 ? 0.55 : 0.4}
              filter={isHighest ? 'url(#pp-radar-glow)' : undefined}
              className="pp-draw-bar"
            />
            <text
              x={valX}
              y={29}
              fontFamily="JetBrains Mono"
              fontSize={12}
              fontWeight={700}
              fill={fill}
              opacity={isHighest ? 0.85 : 0.7}
            >
              {score.toFixed(1)}
            </text>
          </g>
        );
      })}
      <text x={6} y={62} fontFamily="JetBrains Mono" fontSize={11} fill={C.textDim} letterSpacing={0.5}>
        COMPOSITE
      </text>
      <rect x={6} y={67} width={168} height={4} rx={2} fill={C.ruleBright} />
      <rect
        x={6}
        y={67}
        width={compWidth}
        height={4}
        rx={2}
        fill={C.mustard}
        opacity={0.55}
        filter="url(#pp-radar-glow)"
        className="pp-draw-bar"
      />
      <text x={6 + compWidth + 4} y={76} fontFamily="JetBrains Mono" fontSize={12} fontWeight={700} fill={C.mustard} opacity={0.75}>
        {composite.toFixed(1)}
      </text>
    </svg>
  );
}
